import { useEffect, useState } from "react";
import axios from "axios";
import { ph } from "@/lib/posthog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_META = {
  read: { name: "Read", price: "$499 AUD/mo" },
  operator: { name: "Operator", price: "$999 AUD/mo" },
  operator_plus: { name: "Operator+", price: "$1,499 AUD/mo" },
};

/**
 * Modal that captures an email (and optional business name) then POSTs
 * /api/billing/checkout and redirects to the returned Stripe URL.
 *
 * Opened from the three Pricing tier "Get started" buttons.
 */
export const CheckoutModal = ({ plan, onClose }) => {
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const meta = PLAN_META[plan] || { name: plan, price: "" };

  useEffect(() => {
    if (!plan) return;
    ph("checkout_modal_opened", { plan });
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [plan, onClose]);

  if (!plan) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!/.+@.+\..+/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      ph("checkout_started", { plan, email });
      const resp = await axios.post(`${API}/billing/checkout`, {
        plan,
        email: email.trim().toLowerCase(),
        business_name: businessName.trim() || undefined,
        origin: window.location.origin,
      });
      if (resp.data?.url) {
        // Remember the session_id so /welcome can poll it even if Stripe drops the param
        try { sessionStorage.setItem("cv_last_session", resp.data.session_id); } catch (_e) { /* ignore */ }
        window.location.assign(resp.data.url);
      } else {
        setError("Couldn't start checkout. Try again.");
        setSubmitting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't start checkout. Try again.";
      setError(detail);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="cv-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cv-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="checkout-modal"
    >
      <div className="cv-modal">
        <button className="cv-modal-x" aria-label="Close" onClick={onClose} data-testid="checkout-modal-close">×</button>
        <div className="cv-modal-eyebrow">You're picking · {meta.name}</div>
        <h2 id="cv-modal-title">Your email to get started.</h2>
        <p className="cv-modal-sub">Month to month at {meta.price}. Cancel any time. You'll set up your product login after payment.</p>
        <form onSubmit={handleSubmit} noValidate>
          <label className="cv-field">
            <span>Email</span>
            <input
              type="email"
              autoFocus
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbusiness.com.au"
              data-testid="checkout-email-input"
            />
          </label>
          <label className="cv-field">
            <span>Business name <em>(optional)</em></span>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Travel Trucks Pty Ltd"
              data-testid="checkout-business-input"
            />
          </label>
          {error ? <div className="cv-error" role="alert" data-testid="checkout-error">{error}</div> : null}
          <button
            type="submit"
            className="pill"
            style={{ width: "100%", justifyContent: "center", marginTop: 6 }}
            disabled={submitting}
            data-testid="checkout-submit"
          >
            {submitting ? "Opening secure checkout…" : "Continue to secure checkout"}
          </button>
          <p className="cv-fine">Payments handled by Stripe. We never see your card number.</p>
        </form>
      </div>
    </div>
  );
};

export default CheckoutModal;
